// Copyright (c) 2018 Comsearch, Inc., All rights reserved

import administrationDataForForm from './administrationDataForForm'
import antennaDataForForm from './antennaDataForForm'
import ebandDataForForm from './ebandDataForForm'
import frequencyPlanDataForForm from './frequencyPlanDataForForm'
import lossDataForForm from './lossDataForForm'
import passiveRepeatersForForm from './passiveRepeatersForForm'
import pathCalculationsForForm from './pathCalculationsForForm'
import regulatoryDataFormForm from './regulatoryDataFormForm'
import siteDataForForm from './siteDataForForm'
import { radioDataForForm } from 'redux/modules/pathDetails'
import { makeTimestamp } from 'helpers/timeConversion'

import {
  precisionKm,
  precisionMi,
} from 'config/constants'

import {
  castToString,
  roundAndLeaveTrailingZeroes,
} from 'helpers/reduxFormFunctions'

const prepPathDetailsForForm = (pathData, unitType, domainId) => {
  const {
    createTimestamp,
    createUser,
    deleted,
    lastModifiedTimestamp,
    lastModifiedUser,
    pathId,
    pathStatus: { pathStatusId },
    pplFlippedFlag,
    pplPathId,
    pplRcn,
    pplSite1Name,
    pplSite2Name,
  } = pathData

  // the JSON doesn't necessarily have the segments/segmentEnds in the right order here.
  // Sort this before going any further
  // order the segments by their defined order
  const segments = pathData.segments.sort((s1, s2) => s1.segmentNumber - s2.segmentNumber)
  // order each segment's segment ends by their defined order.
  segments.forEach(s => s.segmentEnds.sort((se1, se2) => se1.segmentEndNumber - se2.segmentEndNumber))

  // this will be necessary when sending the segments back to the API
  const segmentIds = segments.map(d => d.segmentId)
  
  const segmentStatusId1 = segments[0].segmentEnds[0].segmentStatus?.segmentStatusAbbreviation || ''
  const segmentStatusId2 = segments[0].segmentEnds[1].segmentStatus?.segmentStatusAbbreviation || ''

  const segmentStatusIds = [segmentStatusId1, segmentStatusId2]

  const band = castToString(pathData.band?.bandId)
  const jobCode = castToString(pathData.jobCode)
  const pathName = castToString(pathData.pathName)
  const rcnNumber = castToString(pathData.rcnNumber)
  const rcnStatusCode = castToString(pathData.rcnStatus?.rcnStatusCode)

  const pathDistanceInKM = roundAndLeaveTrailingZeroes(pathData.pathDistanceInKM, precisionKm)
  const pathDistanceInMiles = roundAndLeaveTrailingZeroes(pathData.pathDistanceInMiles, precisionMi)
  const pathDistance = (
    pathDistanceInKM && pathDistanceInMiles
      ? `${pathDistanceInKM} km / ${pathDistanceInMiles} mi`
      : '-'
  )

  const lastSegIndex = segments.length - 1

  // AKA Site 1 and Site 2 respectively
  const pathEnd1 = segments[0].segmentEnds[0]
  const pathEnd2 = segments[lastSegIndex].segmentEnds[1]

  // site data sets
  const site1 = siteDataForForm(pathEnd1, unitType, band)
  const site2 = siteDataForForm(pathEnd2, unitType, band)
  const sites = [site1, site2]

  // regulatory settings for path ends
  const regulatory1 = regulatoryDataFormForm(pathEnd1)
  const regulatory2 = regulatoryDataFormForm(pathEnd2)
  const regulatory = [regulatory1, regulatory2]

  // administration information for path ends
  const administration1 = administrationDataForForm(pathEnd1)
  const administration2 = administrationDataForForm(pathEnd2)
  const administration = [administration1, administration2]

  // create passive repeater sets.
  // omitting duplicate data for segment_ends
  // and combining back-to-back repeater's antennas
  const passiveRepeaters = passiveRepeatersForForm(pathData, unitType)

  // path end antenna sets
  // TODO - clean this so it does not add secondSegmentEndAntennaId for site1/site2 antennas.
  const s1Antennas = antennaDataForForm(pathEnd1, unitType)
  const s2Antennas = antennaDataForForm(pathEnd2, unitType)
  const antennas = [s1Antennas, s2Antennas]

  // Only Site 2 can be fully Rx only.
  // Check to see if this site's antennas are all 'Rx only'.
  // If they are, that means we'll initialize the form slightly differently.
  // See radios below.

  // NOTE - antenna transmit mode (modeId) is
  // hard coded here as Rx only
  const isSimplex = s2Antennas.every(d => d.mode === 'Rx only')

  // path end radios, modulations and atpc settings
  const s1Radio = radioDataForForm(pathEnd1)
  const s2Radio = radioDataForForm(pathEnd2)
  const radios = [s1Radio]

  // simplex paths do not have a radio on Site 2
  // but we still need to create s2Radio for the pathCalculations stuff below.
  if (!isSimplex) {
    radios.push(s2Radio)
  }

  // loss sets for both path ends.
  const s1Loss = lossDataForForm(pathEnd1)
  const s2Loss = lossDataForForm(pathEnd2)
  const losses = [s1Loss, s2Loss]

  // channel frequency sets
  const s1Frequencies = frequencyPlanDataForForm(pathEnd1)
  const s2Frequencies = frequencyPlanDataForForm(pathEnd2)
  const frequencyPlans = [s1Frequencies, s2Frequencies]

  const s1PathCalculations = pathCalculationsForForm(pathEnd1, s1Radio, pathEnd2)
  const s2PathCalculations = pathCalculationsForForm(pathEnd2, s2Radio, pathEnd1)
  const pathCalculations = [s1PathCalculations, s2PathCalculations]

  // time stamps
  const createInfo = makeTimestamp(createTimestamp, createUser)
  const lastModifiedInfo = makeTimestamp(lastModifiedTimestamp, lastModifiedUser)

  return {
    administration,
    antennas,
    band,
    createInfo,
    deleted, // if the path was has been soft deleted
    domainId, // so we can know assigned domain from within async validations
    ...ebandDataForForm(pathData),
    frequencyPlans,
    isSimplex,
    jobCode,
    lastModifiedInfo,
    losses,
    passiveRepeaters,
    pathCalculations,
    pathDistance,
    pathId,
    pathName,
    pathStatusId,
    pplFlippedFlag,
    pplPathId,
    pplRcn,
    pplSite1Name,
    pplSite2Name,
    radios,
    rcnNumber,
    rcnStatusCode,
    regulatory,
    segmentIds,
    segmentStatusIds,
    sites,
    unitType,
  }
}

export default prepPathDetailsForForm
